
import { useInquiry } from '@/hooks';
import type { InquiryFormData } from '@/types';
import { cn } from '@/lib/utils';
import { CheckCircle, AlertCircle, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';

interface InquiryFormProps {
  className?: string;
  sourcePage?: string;
  title?: string;
}

export function InquiryForm({ className, title }: InquiryFormProps) {
  const { submitInquiry, isPending, isSuccess, error, resetForm } = useInquiry();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    submitInquiry({
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      message: formData.get('message') as string,
      company: formData.get('company') as string,
    } as InquiryFormData);
  };

  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn("bg-green-50 p-8 text-center border border-green-100", className)}
      >
        <div className="w-16 h-16 bg-green-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="font-display text-2xl text-charcoal mb-2">Teşekkürler!</h3>
        <p className="text-gray-600 mb-6">
          Mesajınız bize ulaştı. Satış ekibimiz en kısa sürede sizinle iletişime geçecektir.
        </p>
        <button
          onClick={resetForm}
          className="text-green-700 font-medium hover:text-green-800 underline decoration-green-200 underline-offset-4"
        >
          Yeni bir mesaj gönder
        </button>
      </motion.div>
    );
  }

  return (
    <div className={cn("bg-white p-8 md:p-10 shadow-sm border border-gray-100", className)}>
      <div className="mb-8 border-b border-gray-100 pb-6">
        <h3 className="font-display text-3xl text-charcoal">{title || 'Bize Ulaşın'}</h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <input
            id="name"
            type="text"
            name="name"
            required
            className="w-full px-0 py-3 border-b border-gray-300 focus:border-charcoal outline-none transition-all placeholder:text-gray-400 text-lg bg-transparent"
            placeholder="Adınız Soyadınız"
          />
        </div>

        <div className="space-y-2">
          <input
            id="phone"
            type="tel"
            name="phone"
            required
            className="w-full px-0 py-3 border-b border-gray-300 focus:border-charcoal outline-none transition-all placeholder:text-gray-400 text-lg bg-transparent"
            placeholder="Telefon Numaranız"
          />
        </div>

        <div className="space-y-2">
          <input
            id="email"
            type="email"
            name="email"
            required
            className="w-full px-0 py-3 border-b border-gray-300 focus:border-charcoal outline-none transition-all placeholder:text-gray-400 text-lg bg-transparent"
            placeholder="E-posta Adresiniz"
          />
        </div>

        <div className="space-y-2">
          <textarea
            id="message"
            name="message"
            rows={3}
            required
            className="w-full px-0 py-3 border-b border-gray-300 focus:border-charcoal outline-none transition-all placeholder:text-gray-400 text-lg bg-transparent resize-none"
            placeholder="Mesajınız..."
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 text-error text-sm bg-red-50 p-3">
            <AlertCircle size={16} />
            <p>{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isPending}
          className={cn(
            'w-full py-5 font-bold text-white text-sm uppercase tracking-widest flex items-center justify-center gap-2 mt-4',
            'bg-charcoal hover:bg-black transition-all',
            'disabled:opacity-70 disabled:cursor-not-allowed'
          )}
        >
          {isPending ? (
            'Gönderiliyor...'
          ) : (
            'Teklif İste'
          )}
        </button>

        <div className="text-center pt-4">
          <a
            href="https://wa.me/905537942161"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-green-600 transition-colors font-medium"
          >
            <MessageCircle size={18} />
            WhatsApp üzerinden de iletişime geçebilirsiniz.
          </a>
        </div>
      </form>
    </div>
  );
}
